import React from "react";

import { Country } from "../../features/auth/store/types/country-types";

type CountryTableProps = {
  items: Country[];
  loading?: boolean;
  onEdit?: (item: Country) => void;
  onDelete?: (item: Country) => void;
};

export const CountryTable: React.FC<CountryTableProps> = ({
  items,
  loading,
  onEdit,
  onDelete,
}) => {
  const hasItems = items && items.length > 0;

  return (
    <div className="overflow-hidden bg-white dark:bg-zinc-900 rounded-lg border border-gray-200 dark:border-zinc-800">
      <table className="min-w-full text-left border-collapse">
        {/* Table Header: Hidden on mobile, active as table group on desktop layout */}
        <thead className="hidden md:table-header-group bg-gray-50 dark:bg-zinc-800/50 text-xs uppercase text-gray-500 dark:text-zinc-400">
          <tr>
            <th className="px-4 py-3 font-semibold">Name</th>
            <th className="px-4 py-3 font-semibold">Country Code</th>
            <th className="px-4 py-3 font-semibold">Currency</th>
            <th className="px-4 py-3 font-semibold">Flag</th>
            <th className="px-4 py-3 font-semibold text-right">Actions</th>
          </tr>
        </thead>

        {/* Table Body */}
        <tbody className="block md:table-row-group divide-y divide-gray-100 dark:divide-zinc-800">
          {loading ? (
            <tr className="block md:table-row">
              <td
                colSpan={5}
                className="block md:table-cell px-4 py-6 text-center"
              >
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
                </div>
              </td>
            </tr>
          ) : hasItems ? (
            items.map((country) => (
              <tr
                key={country.id}
                className="block md:table-row text-sm text-gray-800 dark:text-zinc-300 p-4 md:p-0 space-y-2 md:space-y-0"
              >
                {/* Name Row/Cell */}
                <td className="flex justify-between md:table-cell md:px-4 md:py-3 border-b border-gray-50 md:border-none pb-2 md:pb-3">
                  <span className="md:hidden font-medium text-gray-500 dark:text-zinc-400">
                    Name
                  </span>
                  <span className="font-semibold md:font-medium text-right md:text-left">
                    {country.name}
                  </span>
                </td>

                {/* Country Code Row/Cell */}
                <td className="flex justify-between md:table-cell md:px-4 md:py-3 border-b border-gray-50 md:border-none pb-2 md:pb-3">
                  <span className="md:hidden font-medium text-gray-500 dark:text-zinc-400">
                    Country Code
                  </span>
                  <span className="font-semibold dark:text-zinc-100 text-right md:text-left">
                    {country.symbol}
                  </span>
                </td>

                {/* Currency Row/Cell */}
                <td className="flex justify-between md:table-cell md:px-4 md:py-3 border-b border-gray-50 md:border-none pb-2 md:pb-3">
                  <span className="md:hidden font-medium text-gray-500 dark:text-zinc-400">
                    Currency
                  </span>
                  <span className="text-right md:text-left">
                    {country.currency?.name || country.currency?.symbol || "-"}
                  </span>
                </td>

                {/* Flag Row/Cell */}
                <td className="flex justify-between items-center md:table-cell md:px-4 md:py-3 border-b border-gray-50 md:border-none pb-2 md:pb-3">
                  <span className="md:hidden font-medium text-gray-500 dark:text-zinc-400">
                    Flag
                  </span>
                  <div className="flex items-center justify-end md:justify-start gap-3">
                    {country.image_url ? (
                      <img
                        src={country.image_url}
                        alt={`${country.name} flag`}
                        className="size-[40px] object-cover rounded-lg border dark:border-zinc-700"
                      />
                    ) : (
                      <span className="text-gray-400 dark:text-zinc-500 italic text-xs">
                        No Flag
                      </span>
                    )}
                  </div>
                </td>

                {/* Actions Row/Cell */}
                <td className="flex justify-between items-center md:table-cell md:px-4 md:py-3 pt-2 md:pt-3">
                  <span className="md:hidden font-medium text-gray-500 dark:text-zinc-400">
                    Actions
                  </span>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      className="px-3 py-1 text-xs rounded bg-yellow-50 text-yellow-700 border border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-500 dark:border-yellow-900/30"
                      onClick={() => onEdit?.(country)}
                    >
                      Edit
                    </button>
                    <button
                      className="px-3 py-1 text-xs rounded bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-900/30"
                      onClick={() => onDelete?.(country)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr className="block md:table-row">
              <td
                colSpan={5}
                className="block md:table-cell px-4 py-6 text-center text-sm text-gray-500 dark:text-zinc-500"
              >
                No countries available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
